/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import AddressSearch, { IFormattedAddressDetails } from '../../components/citta-address-search/citta-address-search';
import { submitSubscribeFormAction, SubmitSubscribeFormDataActionInput } from '../../data-actions/subscribe-newsletter/submit-subscribe-form-data';
import { GetCookie } from '../../shared-utils/cookie-utilities';
import { ICittaSubscribeNewsletterData } from './citta-subscribe-newsletter.data';
import { ICittaSubscribeNewsletterProps } from './citta-subscribe-newsletter.props.autogenerated';

interface ICittaSubscribeNewsletterFormState {
    firstName: string;
    lastName: string;
    emailAddress: string;
    streetNumber: string;
    streetAddress: string;
    suburb: string;
    cityState: string;
    postCode: string;
    country: string;
    buildingCompliment: string;
    privacyPolicyChecked: boolean;
    fNError: boolean;
    lNError: boolean;
    eAError: boolean;
    reqError: boolean;
    eAValError: boolean;
    pPCError: boolean;
    rCCError: boolean;
    responseReceived: boolean;
    responseError: boolean;
    reCaptchaChecked: boolean;
    manualAddress: boolean;
    allowAddressSearch: boolean;
}

export interface ICittaSubscribeNewsletterForm {
    FormWrapper: INodeProps;
    FormContainer: INodeProps;
    nameInput: React.ReactNode;
    emailInput: React.ReactNode;
    addressInput: React.ReactNode;
    checkboxPrivacyPolicy: React.ReactNode;
    reCaptcha: React.ReactNode;
    subscribeButtton: React.ReactNode;
}

export interface ICittaSubscribeNewsletterViewProps extends ICittaSubscribeNewsletterProps<ICittaSubscribeNewsletterData> {
    SubscribeWrapper: IModuleProps;
    subscribeResponse: React.ReactNode;
    subscribeForm: ICittaSubscribeNewsletterForm;
}
/**
 *
 * CittaSubscribeNewsletter component
 * @extends {React.PureComponent<ICittaSubscribeNewsletterProps<ICittaSubscribeNewsletterData>>}
 */
@observer
class CittaSubscribeNewsletter extends React.PureComponent<ICittaSubscribeNewsletterProps<ICittaSubscribeNewsletterData>, ICittaSubscribeNewsletterFormState> {
    private _FirstNameInputRef: React.RefObject<HTMLInputElement>;
    private _LastNameInputRef: React.RefObject<HTMLInputElement>;
    private _EmailAddressInputRef: React.RefObject<HTMLInputElement>;
    private _StreetNumberInputRef: React.RefObject<HTMLInputElement>;
    private _StreetAddressInputRef: React.RefObject<HTMLInputElement>;
    private _SuburbInputRef: React.RefObject<HTMLInputElement>;
    private _CityStateInputRef: React.RefObject<HTMLInputElement>;
    private _PostCodeInputRef: React.RefObject<HTMLInputElement>;
    private _CountryInputRef: React.RefObject<HTMLSelectElement>;
    private _BuildingComplimentInputRef: React.RefObject<HTMLInputElement>;
    private _PrivacyPolicyRef: React.RefObject<HTMLInputElement>;
    private _SearchAddressRef: React.RefObject<AddressSearch>;
    private _recaptchaRef: React.RefObject<ReCAPTCHA>;

    private firstNameValue: boolean = false;
    private lastNameValue: boolean = false;
    private emailAddressValue: boolean = false;

    private formInputValues: boolean = false;
    private requiredErrorMessage: string | undefined;
    private eAValidationErrorMessage: string | undefined;
    private privacyPolicyErrorMessage: string | undefined;
    private recaptchaErrorMessage: string | undefined;

    private responseMessage: string | undefined;

    private _cookieName: string = this.props.context.app.config.marketPreferenceCookieName || '_msdyn365___market_preference';

    private moduleClassName: string = 'subscribeWrap';

    constructor(props: ICittaSubscribeNewsletterProps<ICittaSubscribeNewsletterData>) {
        super(props);
        this._FirstNameInputRef = React.createRef();
        this._LastNameInputRef = React.createRef();
        this._EmailAddressInputRef = React.createRef();
        this._StreetNumberInputRef = React.createRef();
        this._StreetAddressInputRef = React.createRef();
        this._SuburbInputRef = React.createRef();
        this._CityStateInputRef = React.createRef();
        this._PostCodeInputRef = React.createRef();
        this._CountryInputRef = React.createRef();
        this._BuildingComplimentInputRef = React.createRef();
        this._PrivacyPolicyRef = React.createRef();
        this._SearchAddressRef = React.createRef();
        this._recaptchaRef = React.createRef();
        this._handleSubscribe = this._handleSubscribe.bind(this);
        this._handleSearchAddressSelection = this._handleSearchAddressSelection.bind(this);
        this._toggleManualAddressInput = this._toggleManualAddressInput.bind(this);

        this.state = {
            firstName: '',
            lastName: '',
            emailAddress: '',
            streetNumber: '',
            streetAddress: '',
            suburb: '',
            cityState: '',
            postCode: '',
            country: '',
            buildingCompliment: '',
            privacyPolicyChecked: false,
            fNError: false,
            lNError: false,
            eAError: false,
            reqError: false,
            eAValError: false,
            pPCError: false,
            rCCError: false,
            responseReceived: false,
            responseError: false,
            reCaptchaChecked: false,
            manualAddress: false,
            allowAddressSearch: true
        };
    }

    public componentDidMount(): void {
        const market = GetCookie(this.props.context.request.cookies, this._cookieName);
        if (market) {
            this.setState({
                country: market
            });
            if (market === 'AUS') {
                this.setState({
                    allowAddressSearch: false
                });
            }
        }
    }

    public render(): JSX.Element | null {

        const viewProps: ICittaSubscribeNewsletterViewProps = {
            ...(this.props as ICittaSubscribeNewsletterProps<ICittaSubscribeNewsletterData>),
            SubscribeWrapper: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName)
            },
            subscribeResponse: this._getSubscribeResponse(),
            subscribeForm: this._renderForm(),
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _getSubscribeResponse(): React.ReactNode {
        return (
            this.state.responseReceived && this.responseMessage !== '' || undefined
                ? (
                    <div className={`response-message ${this.state.responseError ? 'response-error' : ''}`}>
                        <p className='response-message-content'>{this.responseMessage}</p>
                    </div>
                )
                : null
        );
    }

    /**
     * callback function for receiving address details from address search component
     */
    private _handleSearchAddressSelection(address: IFormattedAddressDetails): void {
        if (address) {
            this.setState({
                streetNumber: address.street_number ? address.street_number : '',
                streetAddress: address.street ? address.street : '',
                suburb: address.suburb ? address.suburb : '',
                cityState: address.city ? address.city : '',
                postCode: address.postcode ? address.postcode : '',
                country: address.country ? 'NZL' : '',
                buildingCompliment: address.building_name ? address.building_name : ''
            });
        }
    }

    private _toggleManualAddressInput(): void {
        this.setState({
            manualAddress: !this.state.manualAddress,
            streetNumber: '',
            streetAddress: '',
            suburb: '',
            cityState: '',
            postCode: '',
            country: '',
            buildingCompliment: ''
        });
    }

    private onRecaptchaChange = (value: string | null) => {
        if (value) {
            this.setState({ reCaptchaChecked: true, rCCError: false });
        } else {
            this.setState({ reCaptchaChecked: false });
        }
    }

    private _renderForm(): ICittaSubscribeNewsletterForm {
        return {
            FormWrapper: {
                className: 'newsletter-subscription',
            },
            FormContainer: {
                className: 'newsletter-subscription-div',
            },
            nameInput: this._renderFirstAndLastNameInput(),
            emailInput: this._renderEmailInput(),
            addressInput: this._renderAddressInput(),
            checkboxPrivacyPolicy: this._renderCheckboxPrivacyPolicy(),
            reCaptcha: this._renderReCaptcha(),
            subscribeButtton: this._renderSubscribeButton(),
        };
    }

    private _renderFirstAndLastNameInput(): React.ReactNode {
        return (
            <div className='first-and-last-name-input'>
                <div className='name-input-error'>
                    <span className='first-name-input-error'>
                        {this.state.fNError ? this._renderAlert('first-name-input') : ''}
                    </span>
                    <span className='last-name-input-error'>
                        {this.state.lNError ? this._renderAlert('last-name-input') : ''}
                    </span>
                </div>
                <div className='first-name-input'>
                    <input
                        ref={this._FirstNameInputRef}
                        type='text'
                        onChange={this._handleChange('firstName')}
                        aria-label='First Name'
                        className='first-name-input-text'
                        placeholder={this.props.resources.firstNamePlaceholderText}
                        value={this.state.firstName}
                        required
                    />
                </div>
                <div className='last-name-input'>
                    <input
                        ref={this._LastNameInputRef}
                        type='text'
                        onChange={this._handleChange('lastName')}
                        aria-label='Last Name'
                        className='last-name-input-text'
                        placeholder={this.props.resources.lastNamePlaceholderText}
                        value={this.state.lastName}
                        required
                    />
                </div>
            </div>
        );
    }

    private _renderEmailInput(): React.ReactNode {
        return (
            <div className='email-address-input'>
                {this.state.eAError || this.state.eAValError ? this._renderAlert('email-address-input') : ''}
                <input
                    ref={this._EmailAddressInputRef}
                    type='email'
                    onChange={this._handleChange('emailAddress')}
                    aria-label='Email Address'
                    className='email-address-input-text'
                    placeholder={this.props.resources.emailAddressPlaceholderText}
                    value={this.state.emailAddress}
                    required
                />
            </div>
        );
    }

    // tslint:disable-next-line: max-func-body-length
    private _renderAddressInput(): React.ReactNode {
        const showManualAddress = this.state.manualAddress || !this.state.allowAddressSearch;
        return (
            <div>
                {showManualAddress &&
                    <div className='manual-input'>
                        <div className='building-compliment-input'>
                            <input
                                ref={this._BuildingComplimentInputRef}
                                type='text'
                                onChange={this._handleChange('buildingCompliment')}
                                aria-label='Building Compliment'
                                className='building-compliment-input-text'
                                placeholder={this.props.resources.buildingComplimentPlaceholderText}
                                value={this.state.buildingCompliment}
                            />
                        </div>
                        <div className='street-input'>
                            <div className='street-number-input'>
                                <input
                                    ref={this._StreetNumberInputRef}
                                    type='text'
                                    aria-label='Street Number'
                                    onChange={this._handleChange('streetNumber')}
                                    className='street-number-input-text'
                                    placeholder={this.props.resources.streetNumberPlaceholderText}
                                    value={this.state.streetNumber}
                                />
                            </div>
                            <div className='street-address-input'>
                                <input
                                    ref={this._StreetAddressInputRef}
                                    type='text'
                                    aria-label='Street Address'
                                    onChange={this._handleChange('streetAddress')}
                                    className='street-address-input-text'
                                    placeholder={this.props.resources.streetAddressPlaceholderText}
                                    value={this.state.streetAddress}
                                />
                            </div>
                        </div>
                        <div className='suburb-input'>
                            <input
                                ref={this._SuburbInputRef}
                                type='text'
                                onChange={this._handleChange('suburb')}
                                aria-label='Suburb'
                                className='suburb-input-text'
                                placeholder={this.props.resources.suburbPlaceholderText}
                                value={this.state.suburb}
                            />
                        </div>
                        <div className='city-state-input'>
                            <input
                                ref={this._CityStateInputRef}
                                type='text'
                                onChange={this._handleChange('cityState')}
                                aria-label='City State'
                                className='city-state-input-text'
                                placeholder={this.props.resources.cityStatePlaceholderText}
                                value={this.state.cityState}
                            />
                        </div>
                        <div className='postcode-input'>
                            <input
                                ref={this._PostCodeInputRef}
                                type='tel'
                                onChange={this._handleChange('postCode')}
                                aria-label='Post Code'
                                className='postcode-input-text'
                                pattern='[0-9]*'
                                max='9999'
                                placeholder={this.props.resources.postCodePlaceholderText}
                                value={this.state.postCode}
                            />
                        </div>
                        <div className='country-input'>
                            <select
                                ref={this._CountryInputRef}
                                onChange={this._onCountryChange}
                                aria-label='Country'
                                className='country-input-text'
                                placeholder={this.props.resources.countryPlaceholderText}
                                value={this.state.country}
                                required
                            >
                                <option aria-selected='true' value='Country' disabled>Country</option>
                                <option aria-selected='true' value='NZL'>New Zealand</option>
                                <option aria-selected='true' value='AUS'>Australia</option>
                            </select>
                        </div>
                    </div>}
                {this.state.allowAddressSearch &&
                    <AddressSearch
                        ref={this._SearchAddressRef}
                        context={this.props.context}
                        manualAddress={this.state.manualAddress}
                        addressSelectedCallback={this._handleSearchAddressSelection}
                        toggleManualAddressCallback={this._toggleManualAddressInput}
                    />}
            </div>
        );
    }

    private _renderCheckboxPrivacyPolicy(): React.ReactNode {
        return (
            <div className='checkbox-privacy-policy'>
                {this.state.pPCError ? this._renderAlert('checkbox-privacy-policy') : ''}
                <div className='checkbox-privacy-policy-content'>
                    <input
                        ref={this._PrivacyPolicyRef}
                        type='checkbox'
                        className='privacy-policy-text'
                        aria-label='Accept Privacy Policy'
                        aria-checked='false'
                        onChange={this._handleChange('privacyPolicy')}
                        id='privacyPolicyCheckbox'
                    />
                    <span className='checkbox-privacy-policy-text'>
                        <p>I accept the <a href={this.props.config.privacyPolicyLink}>Privacy Policy</a></p>
                    </span>
                </div>
            </div>
        );
    }

    private _renderReCaptcha(): React.ReactNode {
        return (
            <div>
                {
                    this.props.config.reCaptchaOption ? <div className='recaptcha'>
                        {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}
                        <ReCAPTCHA
                            ref={this._recaptchaRef}
                            sitekey={this.props.config.reCaptchaSiteKey!}
                            onChange={this.onRecaptchaChange}
                        />
                    </div> : null
                }
            </div>
        );
    }

    private _renderSubscribeButton(): React.ReactNode {
        return (
            <div>
                <Button
                    className='subscribe-button'
                    title={this.props.config.subscribeButtonLabel}
                    color='primary'
                    onClick={this._handleSubscribe}
                >{this.props.config.subscribeButtonLabel}
                </Button>
            </div>
        );
    }

    private _handleChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        switch (name) {
            case 'firstName': {
                this.setState({ firstName: value, fNError: false, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'lastName': {
                this.setState({ lastName: value, lNError: false, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'emailAddress': {
                this.setState({ emailAddress: value, eAError: false, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'streetNumber': {
                this.setState({ streetNumber: value, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'streetAddress': {
                this.setState({ streetAddress: value, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'suburb': {
                this.setState({ suburb: value, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'cityState': {
                this.setState({ cityState: value, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'postCode': {
                this.setState({ postCode: value, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'country': {
                this.setState({ country: value, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'buildingCompliment': {
                this.setState({ buildingCompliment: value, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            case 'privacyPolicy': {
                const isChecked = this._PrivacyPolicyRef.current && this._PrivacyPolicyRef.current.checked ? this._PrivacyPolicyRef.current.checked : false;
                this.setState({ privacyPolicyChecked: isChecked, reqError: false, eAValError: false, pPCError: false });
                break;
            }
            default: {
                console.log('No input box changes');
                this.setState({ reqError: false, eAValError: false, pPCError: false });
            }
        }
    };

    private _onCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({
            country: e.target.value
        });
    }

    private _isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private _renderAlert(className: string): JSX.Element {
        const inputBoxErrors = (this.state.fNError || this.state.lNError || this.state.eAError);
        if (inputBoxErrors) {
            this.requiredErrorMessage = this.props.resources.requiredTextErrorMessage;
        }
        if (this.state.emailAddress !== '' && this.state.eAValError) {
            this.eAValidationErrorMessage = this.props.resources.eAValidationErrorMessage;
        }
        if (!this.state.privacyPolicyChecked) {
            this.privacyPolicyErrorMessage = this.props.resources.privacyPolicyErrorMessage;
        }
        const reqError = this.state.reqError;
        const eAValError = this.state.eAValError;
        const pPCError = this.state.pPCError;
        return (
            <>
                {reqError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.requiredErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.requiredErrorMessage}
                            </>
                        }
                    </span>
                )}
                {eAValError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.eAValidationErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.eAValidationErrorMessage}
                            </>
                        }
                    </span>
                )}
                {pPCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.privacyPolicyErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.privacyPolicyErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private _renderRecaptchaAlert(className: string): JSX.Element {

        if (this.props.config.reCaptchaOption) {
            if (!this.state.reCaptchaChecked) {
                this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
            }
        }

        const rCCError = this.state.rCCError;
        return (
            <>
                {rCCError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.recaptchaErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.recaptchaErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    // tslint:disable-next-line: cyclomatic-complexity
    private async _handleSubscribe(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>): Promise<void> {
        if (this.state.manualAddress) {
            this.setState({
                country: this._CountryInputRef.current ? this._CountryInputRef.current.value : ''
            });
        }

        this.firstNameValue = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? true : false;
        this.lastNameValue = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? true : false;
        this.emailAddressValue = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? true : false;

        const fN = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? this._FirstNameInputRef.current.value : '';
        const lN = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? this._LastNameInputRef.current.value : '';
        const eA = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? this._EmailAddressInputRef.current.value : '';
        const sN = (this.state.streetNumber && this.state.streetNumber.length > 0) ? this.state.streetNumber : '';
        const sA = (this.state.streetAddress && this.state.streetAddress.length > 0) ? this.state.streetAddress : '';
        const su = (this.state.suburb && this.state.suburb.length > 0) ? this.state.suburb : '';
        const cS = (this.state.cityState && this.state.cityState.length > 0) ? this.state.cityState : '';
        const pC = (this.state.postCode && this.state.postCode.length > 0) ? this.state.postCode : '';
        const co = (this.state.country && this.state.country.length > 0) ? this.state.country : 'NZL';
        const bc = (this.state.buildingCompliment && this.state.buildingCompliment.length > 0) ? this.state.buildingCompliment : '';
        const eAVal = this._EmailAddressInputRef.current!.value;

        if (!this.firstNameValue) {
            this.setState({
                fNError: true,
                reqError: true
            });
        } else if (!this.lastNameValue) {
            this.setState({
                lNError: true,
                reqError: true
            });
        } else if (!this.emailAddressValue) {
            this.setState({
                eAError: true,
                reqError: true
            });
        } else if (!this._isEmailValid(eAVal)) {
            this.setState({
                eAValError: true
            });
        } else if (!this._PrivacyPolicyRef.current!.checked) {
            this.setState({
                privacyPolicyChecked: false,
                pPCError: true
            });
        } else if (this.props.config.reCaptchaOption && !this.state.reCaptchaChecked) {
            this.setState({
                rCCError: true
            });
        } else {
            this.formInputValues = (this.firstNameValue && this.lastNameValue && this.emailAddressValue) ? true : false;

            if (this.formInputValues && this.state.privacyPolicyChecked && !this.state.eAValError) {
                try {
                    const result = await submitSubscribeFormAction(new SubmitSubscribeFormDataActionInput(fN, lN, eA, sN, sA, su, cS, pC, co, bc), this.props.context.actionContext);
                    this.responseMessage = result.Message;
                    this.setState({
                        responseReceived: true,
                        responseError: result.Result === 'Error',
                        firstName: '',
                        lastName: '',
                        emailAddress: '',
                        streetNumber: '',
                        streetAddress: '',
                        suburb: '',
                        cityState: '',
                        postCode: '',
                        country: '',
                        buildingCompliment: '',
                        privacyPolicyChecked: false,
                        reqError: false,
                        eAValError: false,
                        rCCError: false
                    });
                    this._clearValues();
                    this._recaptchaRef.current?.reset();
                } catch {
                    console.log('Please fill the form!');
                }
            }
        }
    }

    private _clearValues(): void {
        if (this._PrivacyPolicyRef.current) {
            this._PrivacyPolicyRef.current.checked = false;
        }
        if (this._SearchAddressRef.current) {
            this._SearchAddressRef.current.clearAddress();
        }
    }
}

export default CittaSubscribeNewsletter;
